import * as React from "react";

import { AgentThreadFeedback } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AgentThreadFeedback";
import { AgentThreadMessageComponents } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AgentThreadMessageComponents.tsx";
import { AssistantToolUseContent } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AssistantToolUseContent.tsx";
import { ForkAgentThreadButton } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/ForkAgentThreadButton";
import { ToolApprovalCard } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/ToolApprovalCard";
import { ToolUseGroup } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/ToolUseGroup";
import { useAIAgentThread } from "@/AgentThread/AgentThreadSidebar/AIAgentThreadContext";
import { ThinkingIndicator } from "@/shared/components";
import {
  AgentThreadMessageStatus,
  AgentThreadRecord,
} from "@/shared/domain/agentThread";
import {
  type AgentThreadGoalRecord,
  type AgentThreadToolUseContent,
  AgentThreadRole,
  AgentThreadStatus,
  isAgentThreadTextContent,
  isAgentThreadToolResultContent,
  isAgentThreadToolUseContent,
} from "@/shared/domain/agentThread/AgentThreadRecord.ts";
import type { PendingToolApproval } from "@/shared/domain/agentThread/hooks/useClientToolExecution";

import styles from "./AgentThreadByIdView.module.css";
import { ContextShiftIndicator } from "./ContextShiftIndicator/ContextShiftIndicator";
import { GoalsStrip } from "./GoalsStrip/GoalsStrip";
import { groupToolMessages } from "./groupToolMessages";
import { ScrollToBottomButton } from "./ScrollToBottomButton";
import { useContextShifts } from "./useContextShifts";
import { useScrollSentinel } from "./useScrollSentinel";
import { useSpacer } from "./useSpacer";

/**
 * Determines if feedback should be shown after a message at the given index.
 * Feedback is shown after assistant messages that end with text content when:
 * 1. The next message is from the user (historical feedback)
 * 2. It's the last message and it's the user's turn (current turn feedback)
 */
function shouldShowFeedback(
  agentThread: AgentThreadRecord,
  messageSequenceNum: number,
): { show: boolean; lastAssistantText?: string } {
  const message = agentThread.messages[messageSequenceNum];
  const nextMessage = agentThread.messages[messageSequenceNum + 1];
  const isLastMessage = messageSequenceNum === agentThread.messages.length - 1;

  // Only show feedback after assistant messages
  if (message.role !== AgentThreadRole.ASSISTANT) {
    return { show: false };
  }

  // Must have content ending with text
  if (message.content.length === 0) {
    return { show: false };
  }

  const lastContent = message.content[message.content.length - 1];
  if (!isAgentThreadTextContent(lastContent)) {
    return { show: false };
  }

  // Show if next message is from user, or if it's the last message and user's turn
  const showAfterHistory =
    nextMessage && nextMessage.role === AgentThreadRole.USER;
  const showAtEnd =
    isLastMessage && agentThread.status === AgentThreadStatus.USER_TURN;

  if (showAfterHistory || showAtEnd) {
    return { show: true, lastAssistantText: lastContent.text };
  }

  return { show: false };
}

interface AgentThreadByIdViewProps {
  agentThread: AgentThreadRecord;
  isCancelling?: boolean;
  onRetry?: (messageSequenceNum: number) => void;
  pendingApproval?: PendingToolApproval | null;
  onApprove?: () => void;
  onDecline?: () => void;
}

export const AgentThreadByIdView = ({
  agentThread,
  isCancelling = false,
  onRetry,
  pendingApproval,
  onApprove,
  onDecline,
}: AgentThreadByIdViewProps) => {
  const { handleLinkClick } = useAIAgentThread();

  // Spacer hook for hoisting user messages to top of viewport
  const { scrollContainerRef, spacerRef, messageToHoistRef, recomputeSpacer } =
    useSpacer();

  // Scroll sentinel hook for detecting when user has scrolled up
  const scrollSentinel = useScrollSentinel(scrollContainerRef);

  // Track user message count to detect when a NEW user message is added
  const userMessageCountRef = React.useRef<number>(0);

  // Track first run to distinguish mount from subsequent updates
  const isFirstRunRef = React.useRef(true);

  // Flag to suppress scroll button flicker during auto-scroll
  const [isAutoScrolling, setIsAutoScrolling] = React.useState(false);

  // Recompute spacer when messages change (streaming, new messages) or when
  // the thread transitions between turns. We attach the hoist ref only while
  // the AI is generating; the status change recompute clears the spacer at
  // rest so the user can't scroll past all messages into an empty viewport.
  React.useLayoutEffect(recomputeSpacer, [
    agentThread.messages,
    agentThread.status,
    recomputeSpacer,
  ]);

  // Handle scroll on mount and when new user messages are added
  React.useLayoutEffect(
    function scrollOnMessagesChange() {
      const currentUserMessageCount = agentThread.messages.filter(
        (m) => m.role === AgentThreadRole.USER,
      ).length;

      if (isFirstRunRef.current) {
        // First mount: initialize count and scroll to bottom
        isFirstRunRef.current = false;
        userMessageCountRef.current = currentUserMessageCount;

        // Only scroll after browser paint to ensure layout has fully applied
        requestAnimationFrame(function scrollToBottom() {
          scrollSentinel.ref.current?.scrollIntoView({
            block: "end",
            behavior: "instant",
          });
        });

        // Skip the rest on first run
        return;
      }

      if (currentUserMessageCount > userMessageCountRef.current) {
        // New user message: scroll to it with smooth animation
        userMessageCountRef.current = currentUserMessageCount;

        // Suppress button during scroll animation
        setIsAutoScrolling(true);

        messageToHoistRef.current?.scrollIntoView({
          block: "start",
          behavior: "smooth",
        });

        // Clear flag after scroll animation completes (estimate ~300ms)
        setTimeout(() => {
          setIsAutoScrolling(false);
        }, 300);
      }
    },
    [agentThread.messages, messageToHoistRef, scrollSentinel.ref],
  );

  const thinking = React.useMemo(() => {
    if (agentThread.messages.length === 0) {
      return true;
    }

    if (agentThread.status === AgentThreadStatus.USER_TURN) {
      return false;
    }

    const lastMessage = agentThread.messages.at(-1);

    // If we're currently addressing a tool use request, that has its own loading indicator, so we shouldn't
    // double-display them
    if (lastMessage) {
      const lastContent = lastMessage?.content.at(-1);

      if (isAgentThreadToolUseContent(lastContent)) {
        return false;
      }
    }

    return lastMessage?.status === AgentThreadMessageStatus.COMPLETED;
  }, [agentThread]);

  // Find the sequence number of the last user message for ref attachment
  const lastUserMessageSequenceNum = React.useMemo(() => {
    for (let i = agentThread.messages.length - 1; i >= 0; i--) {
      if (agentThread.messages[i].role === AgentThreadRole.USER) {
        return i;
      }
    }
    return -1;
  }, [agentThread.messages]);

  // Detect context shifts in the message list (incrementally cached)
  const contextShifts = useContextShifts(agentThread.messages);

  // Goals are session-wide; group them by message_sequence_num so each turn
  // can render its own strip. Empty / missing goals -> empty map -> no strips.
  const goalsByMessageIdx = React.useMemo(() => {
    const map = new Map<number, AgentThreadGoalRecord[]>();
    for (const goal of agentThread.goals ?? []) {
      const list = map.get(goal.message_sequence_num) ?? [];
      list.push(goal);
      map.set(goal.message_sequence_num, list);
    }
    return map;
  }, [agentThread.goals]);

  // ``isGenerating`` covers every non-user turn, not just ROBOTO_TURN, so the
  // hoist-to-top spacer below stays attached while a turn passes through
  // CLIENT_TOOL_TURN. The previous form (``=== ROBOTO_TURN``) caused the
  // spacer to collapse and re-expand each time the agent paused on a client
  // tool, which the user perceived as a layout flicker mid-conversation.
  const isGenerating =
    agentThread.messages.length > 0 &&
    (agentThread.status === AgentThreadStatus.ROBOTO_TURN ||
      agentThread.status === AgentThreadStatus.CLIENT_TOOL_TURN);

  const messageGroups = React.useMemo(
    () => groupToolMessages(agentThread.messages),
    [agentThread.messages],
  );

  const pendingToolUses = React.useMemo((): AgentThreadToolUseContent[] => {
    const lastMessage = agentThread.messages.at(-1);
    const precedingMessage = agentThread.messages.at(-2);

    if (
      lastMessage?.role !== AgentThreadRole.ROBOTO ||
      lastMessage.status !== AgentThreadMessageStatus.GENERATING ||
      precedingMessage?.role !== AgentThreadRole.ASSISTANT
    ) {
      return [];
    }

    const completedToolUseIds = new Set(
      lastMessage.content
        .filter(isAgentThreadToolResultContent)
        .map((content) => content.tool_use_id),
    );

    return precedingMessage.content
      .filter(isAgentThreadToolUseContent)
      .filter((toolUse) => !completedToolUseIds.has(toolUse.tool_use_id));
  }, [agentThread.messages]);

  return (
    <div ref={scrollContainerRef} className={styles.scrollContainer}>
      <div className={styles.centeringContainer}>
        <div
          className={styles.contentContainer}
          role="log"
          aria-label="Chat conversation"
          aria-live="polite"
        >
          {agentThread.messages.length > 0 &&
            messageGroups.map((segment) => {
              if (segment.type === "toolGroup") {
                return (
                  <article
                    key={`tool-group-${segment.messageIndices[0]}`}
                    className={styles.messageArticle}
                  >
                    <ToolUseGroup
                      threadId={agentThread.thread_id}
                      toolResults={segment.toolResults}
                    />
                  </article>
                );
              }

              return segment.messages.map((message, segmentIndex) => {
                const messageSequenceNum = segment.messageIndices[segmentIndex];
                const feedback = shouldShowFeedback(
                  agentThread,
                  messageSequenceNum,
                );
                const isLastMessage =
                  messageSequenceNum === agentThread.messages.length - 1;
                const isLastUserMessage =
                  messageSequenceNum === lastUserMessageSequenceNum;

                // Check if this user message has a context shift to show above it
                const shiftContext = contextShifts.get(messageSequenceNum);
                const goalsForTurn = goalsByMessageIdx.get(messageSequenceNum);

                return (
                  <article
                    key={messageSequenceNum}
                    // Stable selector for "scroll to message N" — querying
                    // ``article``-by-position is wrong because tool groups,
                    // pending tool uses, and approval cards also render as
                    // ``<article>`` elements and shift the index.
                    data-message-sequence-num={messageSequenceNum}
                    ref={
                      // Hoist-to-top is a live-generation affordance only.
                      // Attaching the ref at rest keeps the viewport-sized
                      // spacer alive and lets the user scroll past the last
                      // message into whitespace.
                      isLastUserMessage && isGenerating
                        ? messageToHoistRef
                        : undefined
                    }
                    className={styles.messageArticle}
                  >
                    {shiftContext && (
                      <ContextShiftIndicator context={shiftContext} />
                    )}
                    {goalsForTurn && goalsForTurn.length > 0 && (
                      <GoalsStrip goals={goalsForTurn} />
                    )}
                    <AgentThreadMessageComponents
                      message={message}
                      isLastMessageInAgentThread={isLastMessage}
                      onLinkClick={handleLinkClick}
                      onRetry={
                        onRetry ? () => onRetry(messageSequenceNum) : undefined
                      }
                      isCancelling={isCancelling}
                      pendingApprovalToolUseId={
                        pendingApproval?.toolUse.tool_use_id
                      }
                    />
                    {feedback.show && (
                      <div className={styles.messageActions}>
                        <ForkAgentThreadButton
                          threadId={agentThread.thread_id}
                          agentThreadOrgId={agentThread.org_id}
                          messageSequenceNum={messageSequenceNum}
                          disabled={
                            message.status !==
                            AgentThreadMessageStatus.COMPLETED
                          }
                        />
                        <AgentThreadFeedback
                          threadId={agentThread.thread_id}
                          agentThreadOrgId={agentThread.org_id}
                          messageSequenceNum={messageSequenceNum}
                          messageStatus={message.status}
                        />
                      </div>
                    )}
                  </article>
                );
              });
            })}

          {pendingToolUses
            .filter(
              (toolUse) =>
                toolUse.tool_use_id !== pendingApproval?.toolUse.tool_use_id,
            )
            .map((toolUse) => (
              <article
                key={`pending-tool-${toolUse.tool_use_id}`}
                className={styles.messageArticle}
              >
                <AssistantToolUseContent toolUse={toolUse} />
              </article>
            ))}

          {/* Inline tool approval card */}
          {pendingApproval && onApprove && onDecline && (
            <article
              key={pendingApproval.toolUse.tool_use_id}
              className={styles.messageArticle}
            >
              <ToolApprovalCard
                toolUse={pendingApproval.toolUse}
                tool={pendingApproval.tool}
                onApprove={onApprove}
                onDecline={onDecline}
              />
            </article>
          )}

          {/* Thinking indicator */}
          {thinking && <ThinkingIndicator />}

          {/* Scroll sentinel - detects when scrolled to bottom of content */}
          <div
            ref={scrollSentinel.ref}
            aria-hidden="true"
            style={{ height: "1px", width: "100%", pointerEvents: "none" }}
          />

          {/* Spacer at bottom - height set via direct DOM manipulation */}
          <div ref={spacerRef} aria-hidden="true" style={{ height: 0 }} />
        </div>
      </div>

      <div className={styles.scrollToBottomButtonContainer}>
        <ScrollToBottomButton
          show={!scrollSentinel.isVisible && !isAutoScrolling}
          loading={isGenerating}
          onClick={function scrollToBottom() {
            scrollSentinel.ref.current?.scrollIntoView({
              block: "end",
              behavior: "instant",
            });
          }}
        />
      </div>
    </div>
  );
};
